import React, { useState, useEffect } from 'react';
import { Building2, Users, Scan, Gift, CheckCircle2, DollarSign, TrendingUp, Plus, ExternalLink, ArrowUpRight, MapPin, Copy, Check, Palette, RotateCcw } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import ResetMetricsModal from '../../components/admin/ResetMetricsModal';
import { adminApi } from '../../lib/api';
import { flag } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryUrlCopied, setGalleryUrlCopied] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showResetViewsConfirm, setShowResetViewsConfirm] = useState(false);
  const [resetViewsLoading, setResetViewsLoading] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const galleryUrl = typeof window !== 'undefined' 
    ? `${window.location.origin}/coasters/gallery`
    : 'https://tappd.app/coasters/gallery';

  const copyGalleryUrl = () => {
    navigator.clipboard.writeText(galleryUrl);
    setGalleryUrlCopied(true);
    toast.success('Gallery URL copied to clipboard!');
    setTimeout(() => setGalleryUrlCopied(false), 2000);
  };

  useEffect(() => {
    fetchStats();
  }, [user, isAuthenticated]);

  const fetchStats = async () => {
    try {
      const response = await adminApi.get(`/admin/dashboard/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetViews = async () => {
    setResetViewsLoading(true);
    try {
      const response = await adminApi.post(`/admin/reset-views`, {
        confirm: 'RESET-VIEWS'
      });
      const { deleted, counters_reset } = response.data;
      toast.success(
        `Views reset: ${deleted.business_views} view records deleted, ${counters_reset.businesses} businesses and ${counters_reset.deals} deals zeroed`
      );
      setShowResetViewsConfirm(false);
      fetchStats();
    } catch (err) {
      console.error('Reset views failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to reset views');
    } finally {
      setResetViewsLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-consumer border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!stats) {
    return (
      <AdminLayout>
        <div className="mb-8">
          <p className="font-mono text-consumer text-sm mb-1">// admin.dashboard</p>
          <h1 className="font-display text-3xl text-navy">Dashboard</h1>
          <p className="text-navy/50 mt-1">Welcome back! Here&apos;s an overview of your platform.</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8 text-center">
          <p className="text-lg font-semibold text-yellow-800">Unable to load dashboard statistics</p>
          <p className="text-sm mt-2 text-yellow-700">Please try refreshing the page.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div className="mb-8">
        <p className="font-mono text-consumer text-sm mb-1">// admin.dashboard</p>
        <h1 className="font-display text-3xl text-navy">Dashboard</h1>
        <p className="text-navy/50 mt-1">Welcome back! Here&apos;s an overview of your platform.</p>
      </div>

      {/* Stats Grid - 6 columns on desktop, 2 on mobile */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 md:gap-6 mb-8">
        {/* Active Businesses */}
        <div className="stat-card purple bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-navy/5">
          <div className="flex items-center gap-3 md:block">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-business/10 rounded-xl flex items-center justify-center md:mb-4">
              <Building2 className="w-5 h-5 md:w-6 md:h-6 text-business" />
            </div>
            <p className="text-2xl md:text-4xl font-bold text-navy">{stats.total_businesses}</p>
          </div>
          <p className="text-navy/50 text-xs md:text-sm mt-2 md:mt-0">Active Businesses</p>
        </div>

        {/* Active Users */}
        <div className="stat-card orange bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-navy/5">
          <div className="flex items-center gap-3 md:block">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-consumer/10 rounded-xl flex items-center justify-center md:mb-4">
              <Users className="w-5 h-5 md:w-6 md:h-6 text-consumer" />
            </div>
            <p className="text-2xl md:text-4xl font-bold text-navy">{stats.total_users}</p>
          </div>
          <p className="text-navy/50 text-xs md:text-sm mt-2 md:mt-0">Active Users</p>
        </div>

        {/* Active Venues */}
        <div className="stat-card teal bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-navy/5">
          <div className="flex items-center gap-3 md:block">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-venue/10 rounded-xl flex items-center justify-center md:mb-4">
              <MapPin className="w-5 h-5 md:w-6 md:h-6 text-venue" />
            </div>
            <p className="text-2xl md:text-4xl font-bold text-navy">{stats.total_venues || 0}</p>
          </div>
          <p className="text-navy/50 text-xs md:text-sm mt-2 md:mt-0">Active Venues</p>
        </div>

        {/* Total Scans */}
        <div className="stat-card blue bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-navy/5">
          <div className="flex items-center gap-3 md:block">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-xl flex items-center justify-center md:mb-4">
              <Scan className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
            </div>
            <p className="text-2xl md:text-4xl font-bold text-navy">{stats.total_scans}</p>
          </div>
          <div className="flex items-center justify-between mt-2 md:mt-0">
            <p className="text-navy/50 text-xs md:text-sm">Total Scans</p>
            <span className="text-xs font-medium text-blue-600 hidden md:inline">Today: {stats.scans_today}</span>
          </div>
        </div>

        {/* Offers Claimed */}
        <div className="stat-card green bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-navy/5">
          <div className="flex items-center gap-3 md:block">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-xl flex items-center justify-center md:mb-4">
              <Gift className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
            </div>
            <p className="text-2xl md:text-4xl font-bold text-navy">{stats.total_claims}</p>
          </div>
          <div className="flex items-center justify-between mt-2 md:mt-0">
            <p className="text-navy/50 text-xs md:text-sm">Offers Claimed</p>
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full hidden md:inline">
              {stats.conversion_rate}% conv.
            </span>
          </div>
        </div>

        {/* Redemptions */}
        <div className="stat-card amber bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-navy/5">
          <div className="flex items-center gap-3 md:block">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-100 rounded-xl flex items-center justify-center md:mb-4">
              <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-amber-600" />
            </div>
            <p className="text-2xl md:text-4xl font-bold text-navy">{stats.total_redemptions || 0}</p>
          </div>
          <div className="flex items-center justify-between mt-2 md:mt-0">
            <p className="text-navy/50 text-xs md:text-sm">Redemptions</p>
            <span className="text-xs font-medium text-amber-600 hidden md:inline">
              {stats.total_claims > 0 ? Math.round(((stats.total_redemptions || 0) / stats.total_claims) * 100) : 0}% rate
            </span>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Monthly Revenue */}
        <div className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden">
          <div className="px-6 py-4 border-b border-navy/5">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <h2 className="font-display text-xl text-navy">Monthly Revenue</h2>
            </div>
          </div>
          <div className="p-6">
            <p className="text-4xl font-bold text-navy mb-2">
              ${stats.monthly_revenue?.toLocaleString() || '0'}
            </p>
            <div className="flex items-center gap-2 text-sm">
              <span className="flex items-center gap-1 text-green-600">
                <ArrowUpRight className="w-4 h-4" />
                {stats.revenue_growth || 0}%
              </span>
              <span className="text-navy/50">vs last month</span>
            </div>
            
            {/* Mini revenue breakdown */}
            <div className="mt-6 pt-6 border-t border-navy/5 grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-bold text-navy">${stats.annual_revenue?.toLocaleString() || '0'}</p>
                <p className="text-xs text-navy/50">Annual Revenue</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{stats.active_subscriptions || 0}</p>
                <p className="text-xs text-navy/50">Active Subscriptions</p>
              </div>
            </div>
          </div>
        </div>

        {/* Activity This Month */}
        <div className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden">
          <div className="px-6 py-4 border-b border-navy/5">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-consumer" />
              <h2 className="font-display text-xl text-navy">Activity This Month</h2>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between p-4 bg-cream rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-venue/10 rounded-lg flex items-center justify-center">
                  <Scan className="w-5 h-5 text-venue" />
                </div>
                <div>
                  <p className="font-semibold text-navy">Scans</p>
                  <p className="text-xs text-navy/50">This month</p>
                </div>
              </div>
              <p className="text-2xl font-bold text-navy">{stats.scans_this_month || 0}</p>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-cream rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-consumer/10 rounded-lg flex items-center justify-center">
                  <Gift className="w-5 h-5 text-consumer" />
                </div>
                <div>
                  <p className="font-semibold text-navy">Claims</p>
                  <p className="text-xs text-navy/50">This month</p>
                </div>
              </div>
              <p className="text-2xl font-bold text-navy">{stats.claims_this_month || 0}</p>
            </div>

            <div className="flex items-center justify-between p-4 bg-cream rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-navy">Redemptions</p>
                  <p className="text-xs text-navy/50">This month</p>
                </div>
              </div>
              <p className="text-2xl font-bold text-navy">{stats.redemptions_this_month || 0}</p>
            </div>

            <div className="flex items-center justify-between p-4 bg-cream rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-business/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-business" />
                </div>
                <div>
                  <p className="font-semibold text-navy">New Users</p>
                  <p className="text-xs text-navy/50">This month</p>
                </div>
              </div>
              <p className="text-2xl font-bold text-navy">{stats.new_users_this_month || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Shareable Coaster Gallery Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-navy/5">
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-consumer" />
            <h2 className="font-display text-xl text-navy">Shareable Coaster Gallery</h2>
          </div>
        </div>
        <div className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Preview */}
            <div className="flex-shrink-0">
              <div className="w-32 h-32 bg-gradient-to-br from-[#1e1b4b] to-[#2d2a5e] rounded-2xl flex items-center justify-center shadow-lg">
                <div className="text-center">
                  <Palette className="w-8 h-8 text-white/80 mx-auto mb-2" />
                  <span className="text-white/60 text-xs">Designs</span>
                </div>
              </div>
            </div>
            
            {/* Info & Actions */}
            <div className="flex-1">
              <h3 className="font-display text-lg text-navy mb-2">Share with stakeholders for feedback</h3>
              <p className="text-navy/60 text-sm mb-4">
                Share this link with anyone to get feedback on coaster ideas - no login required.
              </p>
              
              {/* URL Copy Box */}
              {flag('COASTER_GALLERY') && (
              <div className="flex items-center gap-2 p-3 bg-cream rounded-xl mb-4">
                <ExternalLink className="w-4 h-4 text-navy/40 flex-shrink-0" />
                <span className="text-navy/70 text-sm truncate flex-1 font-mono">{galleryUrl}</span>
                <button
                  onClick={copyGalleryUrl}
                  className="px-3 py-1.5 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy/90 transition-colors flex items-center gap-1.5 flex-shrink-0"
                >
                  {galleryUrlCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {galleryUrlCopied ? 'Copied!' : 'Copy URL'}
                </button>
              </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                {flag('COASTER_GALLERY') && (
                <button
                  onClick={() => window.open('/coasters/gallery', '_blank')}
                  className="px-4 py-2 bg-consumer/10 text-consumer rounded-xl text-sm font-semibold hover:bg-consumer/20 transition-colors flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Gallery
                </button>
                )}
                <button
                  onClick={() => navigate('/admin/coaster-designs')}
                  className="px-4 py-2 bg-navy/5 text-navy rounded-xl text-sm font-semibold hover:bg-navy/10 transition-colors"
                >
                  Manage Designs
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Banner */}
      <div className="bg-gradient-to-r from-navy to-[#2d2a5e] rounded-2xl p-6 mb-8">
        <h3 className="font-display text-xl text-white mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate('/admin/businesses')}
            className="px-4 py-2.5 bg-white/10 hover:bg-white/20 rounded-xl text-white text-sm font-medium transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Business
          </button>
          <button
            onClick={() => navigate('/admin/locations')}
            className="px-4 py-2.5 bg-white/10 hover:bg-white/20 rounded-xl text-white text-sm font-medium transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Location
          </button>
          <button
            onClick={() => navigate('/admin/customers')}
            className="px-4 py-2.5 bg-white/10 hover:bg-white/20 rounded-xl text-white text-sm font-medium transition-colors flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            View Users
          </button>
          {flag('REWARDS') && (
          <button
            onClick={() => navigate('/admin/rewards')}
            className="px-4 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl text-white text-sm font-medium hover:shadow-lg hover:shadow-orange-500/20 transition-all flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Manage Rewards
          </button>
          )}
        </div>
      </div>

      {/* Pre-Launch Tools — master admins only */}
      {['hello@tappd.app', 'rauf@iolab.co'].includes(user?.email) && (
        <>
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 space-y-4">
            <h3 className="font-display text-lg text-red-800 mb-1">Pre-Launch Tools</h3>

            {/* Reset Views Only */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-red-800">Reset Page Views</p>
                <p className="text-sm text-red-600/70">Zeros out page views on businesses and deals. Does NOT touch scans, claims, or redemptions.</p>
              </div>
              {!showResetViewsConfirm ? (
                <button
                  onClick={() => setShowResetViewsConfirm(true)}
                  className="flex-shrink-0 ml-4 px-5 py-2.5 border-2 border-amber-300 text-amber-700 rounded-xl text-sm font-semibold hover:bg-amber-100 transition-colors flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset Views
                </button>
              ) : (
                <div className="flex-shrink-0 ml-4 flex items-center gap-2">
                  <button
                    onClick={() => setShowResetViewsConfirm(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-100 transition-colors"
                    disabled={resetViewsLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetViews}
                    disabled={resetViewsLoading}
                    className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <RotateCcw className={`w-4 h-4 ${resetViewsLoading ? 'animate-spin' : ''}`} />
                    {resetViewsLoading ? 'Resetting...' : 'Confirm Reset Views'}
                  </button>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-red-200"></div>

            {/* Reset All Metrics (existing) */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-red-800">Reset All Metrics</p>
                <p className="text-sm text-red-600/70">Nuclear option: clears scans, claims, and redemptions across the entire platform.</p>
              </div>
              <button
                onClick={() => setShowResetModal(true)}
                className="flex-shrink-0 ml-4 px-5 py-2.5 border-2 border-red-300 text-red-700 rounded-xl text-sm font-semibold hover:bg-red-100 transition-colors flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Reset All Metrics
              </button>
            </div>
          </div>

          <ResetMetricsModal
            isOpen={showResetModal}
            onClose={() => setShowResetModal(false)}
            onSuccess={fetchStats}
            stats={stats}
          />
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
